import React from 'react'
import { Box, Checkbox, Flex, PseudoBox, Text } from '@chakra-ui/core'
import useLocaleText from 'components/useLocaleText'
import styled from '@emotion/styled'

const StyledCheckbox = styled(Checkbox)`
  &:focus,
  &:focus-visible,
  &:focus-within,
  & *:focus,
  & *:focus-visible,
  & input:focus,
  & input:focus-visible,
  & [role='checkbox']:focus,
  & [role='checkbox']:focus-visible,
  & [data-focus],
  & [data-focus-visible] {
    box-shadow: none !important;
    outline: none !important;
    border-color: inherit !important;
  }

  & > div:first-of-type {
    &:focus,
    &:focus-visible {
      box-shadow: none !important;
      outline: none !important;
    }
  }
`

export type ReasonVariant = 'reject' | 'warning' | 'info'

interface ReasonCheckboxItemProps {
  reasonKey: string
  isSelected: boolean
  onToggle: () => void
  variant?: ReasonVariant
  score?: number
  showScore?: boolean
}

const VARIANT_COLORS = {
  reject: {
    selectedBg: '#FEE2E2',
    selectedBorder: '#FCA5A5',
    hoverBorder: '#F87171',
    checkboxColor: 'red',
  },
  warning: {
    selectedBg: '#FEF3C7',
    selectedBorder: '#FCD34D',
    hoverBorder: '#FBBF24',
    checkboxColor: 'yellow',
  },
  info: {
    selectedBg: '#DBEAFE',
    selectedBorder: '#93C5FD',
    hoverBorder: '#60A5FA',
    checkboxColor: 'blue',
  },
}

export const ReasonCheckboxItem: React.FC<ReasonCheckboxItemProps> = ({
  reasonKey,
  isSelected,
  onToggle,
  variant = 'warning',
  score,
  showScore = false,
}) => {
  const reasonText = useLocaleText(reasonKey, true)

  const colors = VARIANT_COLORS[variant]

  return (
    <PseudoBox
      as="label"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      p="10px 12px"
      bg={isSelected ? colors.selectedBg : '#F9FAFB'}
      borderRadius="8px"
      border="1px solid"
      borderColor={isSelected ? colors.selectedBorder : '#E5E7EB'}
      cursor="pointer"
      transition="all 0.2s"
      _hover={{
        borderColor: colors.hoverBorder,
        bg: isSelected ? colors.selectedBg : '#F3F4F6',
      }}
    >
      <Flex align="center" style={{ gap: '10px' }}>
        <Box
          as="span"
          css={{
            '& *': {
              outline: 'none !important',
              boxShadow: 'none !important',
            },
            '& *:focus': {
              outline: 'none !important',
              boxShadow: 'none !important',
            },
          }}
        >
          <StyledCheckbox isChecked={isSelected} onChange={onToggle} variantColor={colors.checkboxColor} size="lg" />
        </Box>
        <Text fontSize="14px" fontWeight="500" color="#374151">
          {reasonText}
        </Text>
      </Flex>
      {showScore && score !== undefined && (
        <Box
          px="8px"
          py="2px"
          bg={isSelected ? '#DC2626' : '#9CA3AF'}
          borderRadius="12px"
          minW="40px"
          textAlign="center"
        >
          <Text fontSize="12px" fontWeight="600" color="white">
            -{score}%
          </Text>
        </Box>
      )}
    </PseudoBox>
  )
}
